import {
    Alert,
    Button,
    Checkbox,
    Descriptions,
    Input,
    Radio,
    Space,
    Spin,
    Typography,
    message,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    apiErrorKey,
    describeLiveMessage,
    E_INVOICE_CHANGED_EVENT,
    formatInvoiceAmount,
    formatPayload,
    formatXml,
    type InvoiceLookupRow,
    operationLabelKey,
    type PreviewResult,
    statusLabelKey,
    type TestResult,
    type XmlPreviewResult,
} from '../../domain/eInvoice';
import useRequest from '../../hooks/useRequest';

const { Paragraph, Text } = Typography;

function invoiceLabel(invoice: InvoiceLookupRow): string {
    const amount = formatInvoiceAmount(invoice.amount === null ? null : Number(invoice.amount));

    return [invoice.invoiceNumber, invoice.date, invoice.type, invoice.clientName, amount]
        .filter(part => part !== null && part !== '')
        .join(' · ');
}

function LiveOutcome({ result }: Readonly<{ result: TestResult }>) {
    const { t } = useTranslation();
    const { live } = result;

    if (live === null) {
        return null;
    }

    const detail = describeLiveMessage(live.message);
    const detailText = detail?.kind === 'key' ? t(detail.key) : detail?.text;

    return (
        <Space direction="vertical" style={{ marginTop: 8, width: '100%' }}>
            {live.simulated && (
                <Alert type="warning" showIcon message={t('eInvoice.preview.test.simulated')} />
            )}
            <Alert
                data-testid="e-invoice-test-live"
                type={live.attempted && live.status === 'SUBMITTED' ? 'success' : 'error'}
                showIcon
                message={
                    live.attempted && live.status !== null
                        ? t('eInvoice.preview.test.sent', {
                              status: t(statusLabelKey(live.status)),
                          })
                        : t('eInvoice.preview.test.notSent')
                }
                description={
                    <>
                        {live.providerNumber !== null && (
                            <div>
                                {t('eInvoice.preview.test.providerNumber', {
                                    number: [live.providerSeries, live.providerNumber]
                                        .filter(part => part !== null)
                                        .join(' '),
                                })}
                            </div>
                        )}
                        {detailText && <div>{detailText}</div>}
                    </>
                }
            />
        </Space>
    );
}

function XmlResult({ result }: Readonly<{ result: XmlPreviewResult }>) {
    const { t } = useTranslation();

    return (
        <div data-testid="e-invoice-xml-result">
            {result.waiting && <Alert type="info" showIcon message={t('eInvoice.preview.waiting')} />}
            {result.errors.length > 0 && (
                <Alert
                    data-testid="e-invoice-xml-errors"
                    type="error"
                    showIcon
                    message={t('eInvoice.preview.xml.errorsTitle')}
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {result.errors.map(error => (
                                <li key={error.messageKey}>{t(error.messageKey)}</li>
                            ))}
                        </ul>
                    }
                />
            )}
            {result.xml !== null && (
                <pre
                    data-testid="e-invoice-xml-body"
                    style={{
                        marginTop: 8,
                        maxHeight: 480,
                        overflow: 'auto',
                        background: 'rgba(0, 0, 0, 0.04)',
                        padding: 8,
                    }}
                >
                    {formatXml(result.xml)}
                </pre>
            )}
        </div>
    );
}

/**
 * The exact request a send would make for one invoice, without sending it (plan §7.2, tab 3).
 * This is how a real invoice's payload is checked: nothing here reaches SmartBill. The *Test*
 * button (decision D10) additionally saves that request as a JSON file and, with *Live* ticked,
 * sends the invoice like *Send now*.
 */
export default function EInvoicePreviewTab() {
    const { t } = useTranslation();
    const request = useRequest();
    const [number, setNumber] = useState('');
    const [matches, setMatches] = useState<InvoiceLookupRow[] | null>(null);
    const [preview, setPreview] = useState<PreviewResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [live, setLive] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<TestResult | null>(null);
    const [xmlPreview, setXmlPreview] = useState<XmlPreviewResult | null>(null);
    const [xmlLoading, setXmlLoading] = useState(false);

    const showXml = async (invoiceId: number) => {
        setXmlLoading(true);
        setXmlPreview(null);
        try {
            const result = await request<{ data: XmlPreviewResult }>({
                path: `/e-invoice/invoices/${invoiceId}/preview`,
                params: { format: 'xml' },
            });
            setXmlPreview(result.data);
        } catch (error) {
            console.error('Previewing the CIUS-RO XML failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.preview.failed')));
        } finally {
            setXmlLoading(false);
        }
    };

    const runTest = async (invoiceId: number) => {
        setTesting(true);
        setTestResult(null);
        try {
            const result = await request<{ data: TestResult }>({
                path: `/e-invoice/invoices/${invoiceId}/test`,
                method: 'POST',
                params: { live },
            });
            setTestResult(result.data);
            if (result.data.live?.attempted) {
                globalThis.dispatchEvent(new Event(E_INVOICE_CHANGED_EVENT));
            }
        } catch (error) {
            console.error('Testing an e-invoice failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.preview.test.failed')));
        } finally {
            setTesting(false);
        }
    };

    const showPreview = async (invoiceId: number) => {
        setLoading(true);
        setPreview(null);
        setTestResult(null);
        setXmlPreview(null);
        try {
            const result = await request<{ data: PreviewResult }>({
                path: `/e-invoice/invoices/${invoiceId}/preview`,
                params: { format: 'json' },
            });
            setPreview(result.data);
        } catch (error) {
            console.error('Previewing an e-invoice failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.preview.failed')));
        } finally {
            setLoading(false);
        }
    };

    const find = async (value: string) => {
        const invoiceNumber = value.trim();

        if (invoiceNumber === '') {
            return;
        }

        setLoading(true);
        setMatches(null);
        setPreview(null);
        setTestResult(null);
        setXmlPreview(null);
        try {
            const result = await request<{ data: InvoiceLookupRow[] }>({
                path: '/e-invoice/invoices/lookup',
                params: { number: invoiceNumber },
            });
            setMatches(result.data);
            setLoading(false);
            if (result.data.length === 1) {
                await showPreview(result.data[0].id);
            }
        } catch (error) {
            console.error('Looking up an invoice failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.preview.failed')));
            setLoading(false);
        }
    };

    return (
        <div data-testid="e-invoice-preview">
            <Paragraph type="secondary">{t('eInvoice.preview.description')}</Paragraph>
            <Input.Search
                style={{ maxWidth: 320 }}
                placeholder={t('eInvoice.preview.numberPlaceholder')}
                enterButton={t('eInvoice.preview.show')}
                value={number}
                onChange={event => setNumber(event.target.value)}
                onSearch={value => void find(value)}
                data-testid="e-invoice-preview-number"
            />

            {matches?.length === 0 && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="info"
                    showIcon
                    message={t('eInvoice.preview.notFound')}
                />
            )}
            {matches !== null && matches.length > 1 && (
                <div style={{ marginTop: 12 }}>
                    <Text>{t('eInvoice.preview.pickOne')}</Text>
                    <Radio.Group
                        style={{ display: 'block', marginTop: 8 }}
                        value={preview?.invoiceId}
                        onChange={event => void showPreview(event.target.value)}
                    >
                        <Space direction="vertical">
                            {matches.map(invoice => (
                                <Radio key={invoice.id} value={invoice.id}>
                                    {invoiceLabel(invoice)}
                                </Radio>
                            ))}
                        </Space>
                    </Radio.Group>
                </div>
            )}

            <Spin spinning={loading}>
                {preview && (
                    <div style={{ marginTop: 16 }} data-testid="e-invoice-preview-result">
                        <Descriptions size="small" bordered column={{ xs: 1, md: 2 }}>
                            <Descriptions.Item label={t('eInvoice.queue.invoiceNumber')}>
                                {preview.invoiceNumber}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('eInvoice.queue.operation')}>
                                {t(operationLabelKey(preview.operation))}
                            </Descriptions.Item>
                            {preview.request && (
                                <Descriptions.Item label={t('eInvoice.preview.endpoint')} span={2}>
                                    <Text code>
                                        {preview.request.method} {preview.request.path}
                                    </Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                        {preview.waiting && (
                            <Alert
                                style={{ marginTop: 12 }}
                                type="info"
                                showIcon
                                message={t('eInvoice.preview.waiting')}
                            />
                        )}
                        {preview.error && (
                            <Alert
                                style={{ marginTop: 12 }}
                                type="error"
                                showIcon
                                message={t(preview.error.messageKey)}
                            />
                        )}
                        <Space style={{ marginTop: 12 }} wrap>
                            <Button
                                type="primary"
                                loading={testing}
                                onClick={() => void runTest(preview.invoiceId)}
                                data-testid="e-invoice-test-button"
                            >
                                {t('eInvoice.preview.test.button')}
                            </Button>
                            <Checkbox
                                checked={live}
                                onChange={event => setLive(event.target.checked)}
                                data-testid="e-invoice-test-live-checkbox"
                            >
                                {t('eInvoice.preview.test.live')}
                            </Checkbox>
                        </Space>
                        <Paragraph type="secondary" style={{ marginTop: 4 }}>
                            {t('eInvoice.preview.test.liveHint')}
                        </Paragraph>
                        {testResult && (
                            <div data-testid="e-invoice-test-result">
                                <Alert
                                    type="info"
                                    showIcon
                                    message={
                                        testResult.file
                                            ? t('eInvoice.preview.test.savedTo', testResult.file)
                                            : t('eInvoice.preview.test.nothingSaved')
                                    }
                                    description={
                                        testResult.live === null
                                            ? t('eInvoice.preview.test.offlineDone')
                                            : undefined
                                    }
                                />
                                <LiveOutcome result={testResult} />
                            </div>
                        )}
                        {preview.request && (
                            <pre
                                data-testid="e-invoice-preview-body"
                                style={{
                                    marginTop: 12,
                                    maxHeight: 480,
                                    overflow: 'auto',
                                    background: 'rgba(0, 0, 0, 0.04)',
                                    padding: 8,
                                }}
                            >
                                {formatPayload(preview.request.body) || '-'}
                            </pre>
                        )}
                        <div style={{ marginTop: 16 }}>
                            <Button
                                loading={xmlLoading}
                                onClick={() => void showXml(preview.invoiceId)}
                                data-testid="e-invoice-xml-button"
                            >
                                {t('eInvoice.preview.xml.button')}
                            </Button>
                            <Paragraph type="secondary" style={{ marginTop: 4 }}>
                                {t('eInvoice.preview.xml.description')}
                            </Paragraph>
                            {xmlPreview && <XmlResult result={xmlPreview} />}
                        </div>
                    </div>
                )}
            </Spin>
        </div>
    );
}
